import dayjs from 'dayjs'
import { t } from 'i18next'

import type { GoalConfig } from './types.ts'

export function validateDateByGoalConfig(x: Date, timeSpanType: GoalConfig['timeSpanType']): Date | undefined {
  const type = timeSpanType
  switch (type) {
    case 'day': {
      return x
    }
    case 'none': {
      return x
    }
    case 'month': {
      return dayjs(x).startOf('month').format(`YYYY-MM-DD`) === dayjs(x).format(`YYYY-MM-DD`) ? x : undefined
    }
    case 'week': {
      // startOfが日曜始まりなので、1を追加して(月)始まりにする
      return dayjs(x).startOf('week').add(1, 'day').format(`YYYY-MM-DD`) === dayjs(x).format(`YYYY-MM-DD`)
        ? x
        : undefined
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      throw new Error(type satisfies never)
    }
  }
}

/**
 * - baseDate: 任意の Date(UTCやローカルに対応）
 * - ラベルは JST (= Asia/Tokyo) の「対象月」を示す
 * - value は { startAt, endAt } の形式で、startAtは月初、endAtは次月初の JST 00:00:00.000 を UTC に変換した ISO 文字列
 */
export function createStartAndEndAtOptions(
  baseDate: Date = new Date(),
): Array<{ label: string; value: { startAt: string; endAt: string } }> {
  const JST_OFFSET_MS = 9 * 60 * 60 * 1000

  // baseDate を JST の瞬間に合わせる（JSTでの年月を取得するため）
  const baseDateJst = new Date(baseDate.getTime() + JST_OFFSET_MS)
  const baseYearJst = baseDateJst.getUTCFullYear()
  const baseMonthJst = baseDateJst.getUTCMonth() // 0-11, JST基準

  return [0, 1].map((offset) => {
    // JST上の対象年・月
    const jstMonth = baseMonthJst + offset
    const jstYear = baseYearJst + Math.floor(jstMonth / 12)
    const jstMonthNormalized = ((jstMonth % 12) + 12) % 12 // 0-11

    // startAt用にUTCミリ秒を計算（当月1日 00:00 JST）
    const utcMillisForJstMonthStart = Date.UTC(jstYear, jstMonthNormalized, 1, 0, 0, 0, 0) - JST_OFFSET_MS
    // endAt用にUTCミリ秒を計算（次の月1日 00:00 JST）
    const utcMillisForJstNextMonthStart = Date.UTC(jstYear, jstMonthNormalized + 1, 1, 0, 0, 0, 0) - JST_OFFSET_MS

    const startAt = new Date(utcMillisForJstMonthStart).toISOString()
    const endAt = new Date(utcMillisForJstNextMonthStart).toISOString()

    // ラベルは JST の年/月 表示（先に求めた jstYear / jstMonthNormalized を使う）
    const jstMonthFormatted = String(jstMonthNormalized + 1).padStart(2, '0')
    const label = t(`{{jstYear}}年{{jstMonthFormatted}}月`, {
      jstYear,
      jstMonthFormatted,
    })

    return {
      label,
      value: {
        startAt,
        endAt,
      },
    }
  })
}
